// src/AdminPanelModal.tsx
import React, { useState, useEffect } from 'react';
import {
  AdminStoreConfig,
  HelperConfig,
  loadAdminConfig,
  saveAdminConfig,
  resetAdminConfigToDefaults,
  getGuidelineTabsForCountry,
  getQuestionsForCountry,
} from './data/adminStore';
import { DestinationRequirement } from './data/visaRequirements';
import { Question } from './questions';
import {
  SubmittedApplication,
  loadSubmittedApplications,
  updateApplicationStatus,
  deleteSubmittedApplication,
  exportApplicationsToCSV,
} from './data/applicationsStore';

interface AdminPanelModalProps {
  onClose: () => void;
}

export const AdminPanelModal: React.FC<AdminPanelModalProps> = ({ onClose }) => {
  const [config, setConfig] = useState<AdminStoreConfig>(loadAdminConfig);
  const [activeTab, setActiveTab] = useState<'SUBMISSIONS' | 'MESSAGING' | 'HELPERS' | 'REQUIREMENTS' | 'MARKUP' | 'QUESTIONS' | 'SETTINGS'>('SUBMISSIONS');

  // Submissions State
  const [submissions, setSubmissions] = useState<SubmittedApplication[]>(loadSubmittedApplications);
  const [subSearchQuery, setSubSearchQuery] = useState('');
  const [subCountryFilter, setSubCountryFilter] = useState('ALL');
  const [selectedSubApp, setSelectedSubApp] = useState<SubmittedApplication | null>(null);

  // Helper Add / Edit State
  const [editingHelper, setEditingHelper] = useState<HelperConfig | null>(null);
  const [newCountryName, setNewCountryName] = useState('');
  const [newFlag, setNewFlag] = useState('🌐');
  const [newCategory, setNewCategory] = useState('e-Visa Helper');
  const [newTypes, setNewTypes] = useState('Tourist / Business');
  const [newProcTime, setNewProcTime] = useState('2 - 4 Days');
  const [newBadge, setNewBadge] = useState('Active');

  // Requirements Edit State
  const [selectedReqCountry, setSelectedReqCountry] = useState<string>('INDIA');
  const [reqData, setReqData] = useState<DestinationRequirement>(
    config.customRequirements['INDIA'] || config.customRequirements[Object.keys(config.customRequirements)[0]]
  );

  // New Doc / Rule Inputs
  const [newDocText, setNewDocText] = useState('');
  const [editingDocIndex, setEditingDocIndex] = useState<number | null>(null);
  const [editingDocValue, setEditingDocValue] = useState('');

  // Questions Edit State
  const [selectedQuestionsCountry, setSelectedQuestionsCountry] = useState<'INDIA' | 'MALAYSIA' | 'VIETNAM'>('INDIA');
  const [questionsStep, setQuestionsStep] = useState<number>(1);
  const [questionsList, setQuestionsList] = useState<Question[]>(() => getQuestionsForCountry(config, 'INDIA'));
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editQLabel, setEditQLabel] = useState('');
  const [editQPlaceholder, setEditQPlaceholder] = useState('');
  const [editQTooltip, setEditQTooltip] = useState('');
  const [editQOptions, setEditQOptions] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState('');

  useEffect(() => {
    setQuestionsList(getQuestionsForCountry(config, selectedQuestionsCountry));
    setEditingQuestion(null);
  }, [selectedQuestionsCountry, config]);

  // System Settings State
  const [exchangeRateInput, setExchangeRateInput] = useState<number>(config.exchangeRateLkr);
  const [hideUsdInput, setHideUsdInput] = useState<boolean>(config.hideUsdFromPublic);

  // Messaging State
  const [msgClientPhone, setMsgClientPhone] = useState('');
  const [msgClientName, setMsgClientName] = useState('Dear Client');
  const [msgRefCode, setMsgRefCode] = useState('MYS-83910');
  const [msgCountry, setMsgCountry] = useState('India');
  const [msgFeeLkr, setMsgFeeLkr] = useState('13,600');

  useEffect(() => {
    const handleSync = () => {
      setSubmissions(loadSubmittedApplications());
      setConfig(loadAdminConfig());
    };
    window.addEventListener('submittedAppsUpdated', handleSync);
    window.addEventListener('adminConfigUpdated', handleSync);
    return () => {
      window.removeEventListener('submittedAppsUpdated', handleSync);
      window.removeEventListener('adminConfigUpdated', handleSync);
    };
  }, []);

  useEffect(() => {
    const data = config.customRequirements[selectedReqCountry] || config.customRequirements['INDIA'];
    if (data) setReqData(data);
  }, [selectedReqCountry, config.customRequirements]);

  // Submissions Actions
  const handleStatusChange = (id: string, newStatus: SubmittedApplication['status']) => {
    updateApplicationStatus(id, newStatus);
    setSubmissions(loadSubmittedApplications());
  };

  const handleDeleteSubmission = (id: string) => {
    if (window.confirm('Delete this application submission record?')) {
      deleteSubmittedApplication(id);
      setSubmissions(loadSubmittedApplications());
    }
  };

  const filteredSubmissions = submissions.filter((app) => {
    const matchesCountry = subCountryFilter === 'ALL' || app.destination.toUpperCase() === subCountryFilter;
    const q = subSearchQuery.toLowerCase();
    const matchesQuery =
      !q ||
      app.applicantName.toLowerCase().includes(q) ||
      app.passportNumber.toLowerCase().includes(q) ||
      app.id.toLowerCase().includes(q);
    return matchesCountry && matchesQuery;
  });

  // Helper Actions
  const handleSaveHelperEdit = () => {
    if (!editingHelper) return;
    const updatedHelpers = config.helpers.map((h) => (h.id === editingHelper.id ? editingHelper : h));
    const newConfig = { ...config, helpers: updatedHelpers };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
    setEditingHelper(null);
    alert(`✅ Updated ${editingHelper.country} Visa Helper!`);
  };

  const handleToggleLock = (id: string) => {
    const updatedHelpers = config.helpers.map((h) => (h.id === id ? { ...h, isLocked: !h.isLocked } : h));
    const newConfig = { ...config, helpers: updatedHelpers };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
  };

  const handleMoveHelper = (index: number, direction: 'UP' | 'DOWN') => {
    const newHelpers = [...config.helpers];
    const targetIndex = direction === 'UP' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newHelpers.length) return;

    const temp = newHelpers[index];
    newHelpers[index] = newHelpers[targetIndex];
    newHelpers[targetIndex] = temp;

    newHelpers.forEach((h, i) => (h.order = i + 1));

    const newConfig = { ...config, helpers: newHelpers };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
  };

  const handleDeleteHelper = (id: string) => {
    if (window.confirm('Are you sure you want to remove this visa helper?')) {
      const updatedHelpers = config.helpers.filter((h) => h.id !== id);
      const newConfig = { ...config, helpers: updatedHelpers };
      setConfig(newConfig);
      saveAdminConfig(newConfig);
    }
  };

  const handleAddNewHelper = () => {
    if (!newCountryName.trim()) {
      alert('Please enter country name.');
      return;
    }
    const id = newCountryName.toLowerCase().replace(/\s+/g, '_');
    const newHelper: HelperConfig = {
      id,
      path: `/${id}`,
      country: newCountryName.trim(),
      flag: newFlag || '🌐',
      category: newCategory || 'e-Visa Helper',
      types: newTypes || 'Tourist / Business',
      processingTime: newProcTime || '2 - 4 Days',
      badge: newBadge || 'New',
      badgeBg: '#3b82f6',
      bgGradient: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      isLocked: false,
      order: config.helpers.length + 1,
    };
    const updatedHelpers = [...config.helpers, newHelper];
    const newConfig = { ...config, helpers: updatedHelpers };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
    setNewCountryName('');
    alert(`✅ Added ${newCountryName} Visa Helper!`);
  };

  // Requirements Actions
  const handleUpdateMarkupPrice = (index: number, field: 'govtUsd' | 'agentMarkupUsd' | 'description', val: any) => {
    const updatedPrices = [...reqData.visaPrices];
    updatedPrices[index] = { ...updatedPrices[index], [field]: val };
    const updatedReqData = { ...reqData, visaPrices: updatedPrices };
    setReqData(updatedReqData);

    const updatedCustomReqs = { ...config.customRequirements, [selectedReqCountry]: updatedReqData };
    const newConfig = { ...config, customRequirements: updatedCustomReqs };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
  };

  const handleToggleHidePrice = (index: number) => {
    const updatedPrices = [...reqData.visaPrices];
    updatedPrices[index] = {
      ...updatedPrices[index],
      isHidden: !updatedPrices[index].isHidden,
    };
    const updatedReqData = { ...reqData, visaPrices: updatedPrices };
    setReqData(updatedReqData);

    const updatedCustomReqs = { ...config.customRequirements, [selectedReqCountry]: updatedReqData };
    const newConfig = { ...config, customRequirements: updatedCustomReqs };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
  };

  // Per-category Document Checklist Actions
  const [selectedDocCategoryTab, setSelectedDocCategoryTab] = useState<string>('TOURIST');

  const getActiveDocList = (): string[] => {
    if (reqData.categoryDocuments && reqData.categoryDocuments[selectedDocCategoryTab]) {
      return reqData.categoryDocuments[selectedDocCategoryTab];
    }
    return reqData.requiredDocuments || [];
  };

  const handleUpdateActiveDocList = (newList: string[]) => {
    const currentCatDocs = reqData.categoryDocuments || {};
    const updatedCatDocs = {
      ...currentCatDocs,
      [selectedDocCategoryTab]: newList,
    };
    const updatedReqData: DestinationRequirement = {
      ...reqData,
      categoryDocuments: updatedCatDocs,
      requiredDocuments: selectedDocCategoryTab === 'TOURIST' ? newList : reqData.requiredDocuments,
    };
    setReqData(updatedReqData);

    const updatedCustomReqs = { ...config.customRequirements, [selectedReqCountry]: updatedReqData };
    const newConfig = { ...config, customRequirements: updatedCustomReqs };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
  };

  const handleAddDocumentItem = () => {
    if (!newDocText.trim()) return;
    const currentList = getActiveDocList();
    const updatedDocs = [...currentList, newDocText.trim()];
    handleUpdateActiveDocList(updatedDocs);
    setNewDocText('');
  };

  const handleDeleteDocumentItem = (index: number) => {
    const currentList = getActiveDocList();
    const updatedDocs = currentList.filter((_, i) => i !== index);
    handleUpdateActiveDocList(updatedDocs);
  };

  const handleStartEditDocumentItem = (index: number, currentText: string) => {
    setEditingDocIndex(index);
    setEditingDocValue(currentText);
  };

  const handleSaveDocumentItem = (index: number) => {
    if (!editingDocValue.trim()) return;
    const currentList = getActiveDocList();
    const updatedDocs = [...currentList];
    updatedDocs[index] = editingDocValue.trim();
    handleUpdateActiveDocList(updatedDocs);
    setEditingDocIndex(null);
  };

  // Per-Country Guideline Tab Actions
  const [newGuidelineTabLabel, setNewGuidelineTabLabel] = useState('');

  const handleToggleHideGuidelineTab = (id: string) => {
    const currentTabs = getGuidelineTabsForCountry(config, selectedReqCountry);
    const updatedTabs = currentTabs.map((t) =>
      t.id === id ? { ...t, isHidden: !t.isHidden } : t
    );
    const newCountryTabs = {
      ...(config.countryGuidelineTabs || {}),
      [selectedReqCountry]: updatedTabs,
    };
    const newConfig = { ...config, countryGuidelineTabs: newCountryTabs };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
  };

  const handleAddNewGuidelineTab = () => {
    if (!newGuidelineTabLabel.trim()) return;
    const id = newGuidelineTabLabel.toUpperCase().replace(/[^A-Z0-9]/g, '_');
    const newTab = { id, label: newGuidelineTabLabel.trim(), isHidden: false };
    const currentTabs = getGuidelineTabsForCountry(config, selectedReqCountry);
    const updatedTabs = [...currentTabs, newTab];
    const newCountryTabs = {
      ...(config.countryGuidelineTabs || {}),
      [selectedReqCountry]: updatedTabs,
    };
    const newConfig = { ...config, countryGuidelineTabs: newCountryTabs };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
    setNewGuidelineTabLabel('');
    alert(`✅ Added ${newGuidelineTabLabel} Guideline Tab for ${selectedReqCountry}!`);
  };

  const handleDeleteGuidelineTab = (id: string) => {
    if (window.confirm(`Delete this guideline tab for ${selectedReqCountry}?`)) {
      const currentTabs = getGuidelineTabsForCountry(config, selectedReqCountry);
      const updatedTabs = currentTabs.filter((t) => t.id !== id);
      const newCountryTabs = {
        ...(config.countryGuidelineTabs || {}),
        [selectedReqCountry]: updatedTabs,
      };
      const newConfig = { ...config, countryGuidelineTabs: newCountryTabs };
      setConfig(newConfig);
      saveAdminConfig(newConfig);
    }
  };

  // Question Actions
  const handleStartEditQuestion = (q: Question) => {
    setEditingQuestion(q);
    setEditQLabel(q.label);
    setEditQPlaceholder(q.placeholder || '');
    setEditQTooltip(q.tooltip || '');
    setEditQOptions(q.options ? [...q.options] : []);
    setNewOptionInput('');
  };

  const handleAddQuestionOption = () => {
    if (!newOptionInput.trim()) return;
    setEditQOptions((prev) => [...prev, newOptionInput.trim()]);
    setNewOptionInput('');
  };

  const handleModifyQuestionOption = (index: number, value: string) => {
    setEditQOptions((prev) => prev.map((opt, i) => (i === index ? value : opt)));
  };

  const handleRemoveQuestionOption = (index: number) => {
    setEditQOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveQuestionEdit = () => {
    if (!editingQuestion) return;
    const updatedList = questionsList.map((q) =>
      q.id === editingQuestion.id
        ? {
            ...q,
            label: editQLabel,
            placeholder: editQPlaceholder,
            tooltip: editQTooltip,
            options: editQOptions.length > 0 ? editQOptions : q.options,
          }
        : q
    );
    setQuestionsList(updatedList);
    setEditingQuestion(null);

    const updatedCustomQuestions = {
      ...(config.customQuestions || {}),
      [selectedQuestionsCountry]: updatedList,
    };
    const newConfig = { ...config, customQuestions: updatedCustomQuestions };
    setConfig(newConfig);
    saveAdminConfig(newConfig);

    alert(`✅ Saved rules & selection options for "${editQLabel}" (${selectedQuestionsCountry})!`);
  };

  // Save Settings
  const handleSaveSettings = () => {
    const newConfig = {
      ...config,
      exchangeRateLkr: exchangeRateInput,
      hideUsdFromPublic: hideUsdInput,
    };
    setConfig(newConfig);
    saveAdminConfig(newConfig);
    alert('✅ System settings & currency privacy updated!');
  };

  const handleResetDefaults = () => {
    if (window.confirm('Reset all visa helpers, pricing markup, and settings to factory defaults?')) {
      const reset = resetAdminConfigToDefaults();
      setConfig(reset);
      setExchangeRateInput(reset.exchangeRateLkr);
      setHideUsdInput(reset.hideUsdFromPublic);
      alert('🔄 Restored factory defaults successfully.');
    }
  };

  // WhatsApp Helper
  const openWhatsAppMessage = (text: string) => {
    const phone = msgClientPhone.replace(/[^0-9]/g, '');
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(6px)',
        zIndex: 99999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '1100px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
        }}
      >
        {/* MODAL HEADER */}
        <div
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
            color: '#ffffff',
            padding: '1.25rem 1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.35rem' }}>⚙️</span>
              <h2 style={{ fontSize: '1.3rem', fontWeight: '800', margin: 0, color: '#ffffff' }}>
                mySky Travels Admin Control Center
              </h2>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#93c5fd', marginTop: '0.15rem' }}>
              Submissions Dashboard, Client Messaging, Visa Helpers & Profit Markup Configurator
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.15)',
              border: 'none',
              color: '#ffffff',
              borderRadius: '9999px',
              width: '36px',
              height: '36px',
              fontSize: '1.1rem',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* TABS NAVIGATION */}
        <div style={{ display: 'flex', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', padding: '0 0.5rem', flexWrap: 'wrap' }}>
          {[
            { id: 'SUBMISSIONS', label: '📊 Submissions Dashboard' },
            { id: 'MESSAGING', label: '💬 WhatsApp & Messaging' },
            { id: 'HELPERS', label: '🌐 Visa Helpers Manager' },
            { id: 'MARKUP', label: '💰 Agent Profit Markup' },
            { id: 'REQUIREMENTS', label: '📑 Requirements & Checklist' },
            { id: 'QUESTIONS', label: '📝 Form Questions & Rules' },
            { id: 'SETTINGS', label: '💱 Settings & Currency' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '0.85rem 1rem',
                border: 'none',
                background: 'transparent',
                fontSize: '0.85rem',
                fontWeight: activeTab === tab.id ? '800' : '600',
                color: activeTab === tab.id ? '#1e3a8a' : '#64748b',
                borderBottom: activeTab === tab.id ? '3px solid #2563eb' : '3px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* MODAL BODY CONTENT */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          {/* TAB 0: SUBMISSIONS DASHBOARD */}
          {activeTab === 'SUBMISSIONS' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                    📊 Submitted e-Visa Applications ({submissions.length} Total)
                  </h3>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.2rem' }}>
                    View applicant details, update status, and export submissions to CSV
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => exportApplicationsToCSV(filteredSubmissions)}
                  className="btn-primary"
                  style={{ fontSize: '0.85rem', padding: '0.55rem 1.25rem', background: '#059669' }}
                >
                  📥 Export Selected ({filteredSubmissions.length}) to CSV
                </button>
              </div>

              {/* FILTER BAR */}
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="🔍 Search applicant name, passport no, or reference code..."
                  value={subSearchQuery}
                  onChange={(e) => setSubSearchQuery(e.target.value)}
                  style={{ flex: 1, minWidth: '240px', padding: '0.5rem 0.85rem' }}
                />

                <select
                  value={subCountryFilter}
                  onChange={(e) => setSubCountryFilter(e.target.value)}
                  style={{ width: 'auto', padding: '0.5rem 0.85rem' }}
                >
                  <option value="ALL">All Destinations</option>
                  <option value="INDIA">India 🇮🇳</option>
                  <option value="MALAYSIA">Malaysia 🇲🇾</option>
                  <option value="VIETNAM">Vietnam 🇻🇳</option>
                </select>
              </div>

              {/* SUBMISSIONS TABLE */}
              {filteredSubmissions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', background: '#f8fafc', borderRadius: '12px', border: '2px dashed #e2e8f0' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📑</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: '#475569' }}>No application submissions found</div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Applications submitted by users will appear here automatically.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#0f172a', color: '#ffffff', textAlign: 'left' }}>
                        <th style={{ padding: '0.75rem 0.85rem', borderRadius: '8px 0 0 0' }}>Ref Code</th>
                        <th style={{ padding: '0.75rem 0.85rem' }}>Country</th>
                        <th style={{ padding: '0.75rem 0.85rem' }}>Applicant Name</th>
                        <th style={{ padding: '0.75rem 0.85rem' }}>Passport No</th>
                        <th style={{ padding: '0.75rem 0.85rem' }}>Visa Type</th>
                        <th style={{ padding: '0.75rem 0.85rem' }}>Fee (LKR)</th>
                        <th style={{ padding: '0.75rem 0.85rem' }}>Status</th>
                        <th style={{ padding: '0.75rem 0.85rem', borderRadius: '0 8px 0 0', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubmissions.map((app) => (
                        <tr key={app.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '0.75rem 0.85rem', fontWeight: '800', color: '#1e3a8a' }}>{app.id}</td>
                          <td style={{ padding: '0.75rem 0.85rem', fontWeight: '700' }}>{app.countryName}</td>
                          <td style={{ padding: '0.75rem 0.85rem', fontWeight: '700', color: '#0f172a' }}>{app.applicantName}</td>
                          <td style={{ padding: '0.75rem 0.85rem', fontFamily: 'monospace', fontWeight: '700' }}>{app.passportNumber}</td>
                          <td style={{ padding: '0.75rem 0.85rem', color: '#475569' }}>{app.visaCategory}</td>
                          <td style={{ padding: '0.75rem 0.85rem', fontWeight: '800', color: '#15803d' }}>
                            LKR {app.totalFeeLkr?.toLocaleString()} Rs
                          </td>
                          <td style={{ padding: '0.75rem 0.85rem' }}>
                            <select
                              value={app.status}
                              onChange={(e) => handleStatusChange(app.id, e.target.value as any)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '6px',
                                fontSize: '0.775rem',
                                fontWeight: '700',
                                border: '1px solid #cbd5e1',
                              }}
                            >
                              <option value="Pending 🟡">Pending 🟡</option>
                              <option value="Processing 🔵">Processing 🔵</option>
                              <option value="Approved 🟢">Approved 🟢</option>
                              <option value="Action Required 🔴">Action Required 🔴</option>
                            </select>
                          </td>
                          <td style={{ padding: '0.75rem 0.85rem', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                onClick={() => setSelectedSubApp(app)}
                                style={{ padding: '0.35rem 0.65rem', borderRadius: '6px', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: '700', fontSize: '0.75rem', cursor: 'pointer' }}
                              >
                                👁️ View
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteSubmission(app.id)}
                                style={{ padding: '0.35rem 0.65rem', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontWeight: '700', fontSize: '0.75rem', cursor: 'pointer' }}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 0.5: WHATSAPP & CLIENT MESSAGING */}
          {activeTab === 'MESSAGING' && (
            <div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.35rem' }}>
                💬 Instant WhatsApp & Client Messaging Templates
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1.5rem' }}>
                Quickly send quotes, receipts, status updates, and visa approval notifications to clients via WhatsApp Web!
              </p>

              <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '14px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '0.75rem' }}>
                  👤 Client & Quote Input
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>WhatsApp Phone No:</label>
                    <input type="text" placeholder="e.g. +94771234567" value={msgClientPhone} onChange={(e) => setMsgClientPhone(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Client Name:</label>
                    <input type="text" value={msgClientName} onChange={(e) => setMsgClientName(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Application Ref Code:</label>
                    <input type="text" value={msgRefCode} onChange={(e) => setMsgRefCode(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Destination Country:</label>
                    <input type="text" value={msgCountry} onChange={(e) => setMsgCountry(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Total Fee (LKR):</label>
                    <input type="text" value={msgFeeLkr} onChange={(e) => setMsgFeeLkr(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* WHATSAPP TEMPLATES GRID */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                {/* Template 1: Quote & Receipt */}
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#14532d', marginBottom: '0.5rem' }}>
                    🟢 1. Application Receipt & Fee Quote
                  </h4>
                  <div style={{ background: '#ffffff', border: '1px solid #dcfce7', padding: '0.75rem', borderRadius: '8px', fontSize: '0.8rem', color: '#166534', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>
                    {`Hello ${msgClientName}, your ${msgCountry} e-Visa application (${msgRefCode}) has been received by mySky Travels.\n\nTotal Service Fee: LKR ${msgFeeLkr} Rs.\n\nWe will update you as soon as submission is completed! ✈️`}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      openWhatsAppMessage(
                        `Hello ${msgClientName}, your ${msgCountry} e-Visa application (${msgRefCode}) has been received by mySky Travels.\n\nTotal Service Fee: LKR ${msgFeeLkr} Rs.\n\nWe will update you as soon as submission is completed! ✈️`
                      )
                    }
                    className="btn-primary"
                    style={{ width: '100%', padding: '0.6rem', fontSize: '0.85rem', background: '#16a34a' }}
                  >
                    💬 Send Receipt via WhatsApp
                  </button>
                </div>

                {/* Template 2: Status Update */}
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '0.5rem' }}>
                    🔵 2. Status Update Notification
                  </h4>
                  <div style={{ background: '#ffffff', border: '1px solid #dbeafe', padding: '0.75rem', borderRadius: '8px', fontSize: '0.8rem', color: '#1e40af', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>
                    {`Dear ${msgClientName}, your ${msgCountry} e-Visa application (${msgRefCode}) is currently under PROCESSING with Government Immigration.\n\nEstimated completion: 2 - 3 business days.`}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      openWhatsAppMessage(
                        `Dear ${msgClientName}, your ${msgCountry} e-Visa application (${msgRefCode}) is currently under PROCESSING with Government Immigration.\n\nEstimated completion: 2 - 3 business days.`
                      )
                    }
                    className="btn-primary"
                    style={{ width: '100%', padding: '0.6rem', fontSize: '0.85rem', background: '#2563eb' }}
                  >
                    💬 Send Status Update via WhatsApp
                  </button>
                </div>

                {/* Template 3: Visa Approved */}
                <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '12px', padding: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#d48806', marginBottom: '0.5rem' }}>
                    🎉 3. Visa Approved Notice
                  </h4>
                  <div style={{ background: '#ffffff', border: '1px solid #fffb8f', padding: '0.75rem', borderRadius: '8px', fontSize: '0.8rem', color: '#8c6b00', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>
                    {`🎉 Great news ${msgClientName}! Your ${msgCountry} e-Visa (${msgRefCode}) has been APPROVED by Government Immigration! 📄\n\nThank you for choosing mySky Travels!`}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      openWhatsAppMessage(
                        `🎉 Great news ${msgClientName}! Your ${msgCountry} e-Visa (${msgRefCode}) has been APPROVED by Government Immigration! 📄\n\nThank you for choosing mySky Travels!`
                      )
                    }
                    className="btn-primary"
                    style={{ width: '100%', padding: '0.6rem', fontSize: '0.85rem', background: '#d97706' }}
                  >
                    💬 Send Approval Notice via WhatsApp
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 1: HELPERS MANAGER */}
          {activeTab === 'HELPERS' && (
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', marginBottom: '1rem' }}>
                🌐 Manage Destination Visa Helpers
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.75rem' }}>
                {config.helpers.map((h, index) => (
                  <div
                    key={h.id}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #cbd5e1',
                      borderRadius: '12px',
                      padding: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '0.75rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '2rem' }}>{h.flag}</span>
                      <div>
                        <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a' }}>
                          {h.country} e-Visa {h.isLocked && <span style={{ color: '#ef4444' }}>🔒 [LOCKED]</span>}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {h.category} | {h.processingTime}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => handleToggleLock(h.id)}
                        style={{
                          padding: '0.45rem 0.85rem',
                          borderRadius: '8px',
                          border: 'none',
                          background: h.isLocked ? '#fef2f2' : '#f0fdf4',
                          color: h.isLocked ? '#dc2626' : '#166534',
                          fontWeight: '700',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                        }}
                      >
                        {h.isLocked ? '🔒 Locked' : '🟢 Unlocked'}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleMoveHelper(index, 'UP')}
                        disabled={index === 0}
                        style={{ padding: '0.45rem 0.65rem', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveHelper(index, 'DOWN')}
                        disabled={index === config.helpers.length - 1}
                        style={{ padding: '0.45rem 0.65rem', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                      >
                        ▼
                      </button>

                      <button
                        type="button"
                        onClick={() => setEditingHelper(h)}
                        style={{ padding: '0.45rem 0.85rem', borderRadius: '8px', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer' }}
                      >
                        ✏️ Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteHelper(h.id)}
                        style={{ padding: '0.45rem 0.85rem', borderRadius: '8px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer' }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {editingHelper && (
                <div style={{ background: '#eff6ff', border: '2px solid #2563eb', borderRadius: '14px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '0.75rem' }}>
                    ✏️ Edit {editingHelper.country} Visa Helper Details
                  </h4>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Country Name:</label>
                      <input
                        type="text"
                        value={editingHelper.country}
                        onChange={(e) => setEditingHelper({ ...editingHelper, country: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Flag Emoji:</label>
                      <input
                        type="text"
                        value={editingHelper.flag}
                        onChange={(e) => setEditingHelper({ ...editingHelper, flag: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Category Label:</label>
                      <input
                        type="text"
                        value={editingHelper.category}
                        onChange={(e) => setEditingHelper({ ...editingHelper, category: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Processing Time:</label>
                      <input
                        type="text"
                        value={editingHelper.processingTime}
                        onChange={(e) => setEditingHelper({ ...editingHelper, processingTime: e.target.value })}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" onClick={handleSaveHelperEdit} className="btn-primary" style={{ fontSize: '0.85rem', padding: '0.5rem 1.25rem' }}>
                      💾 Save Helper Changes
                    </button>
                    <button type="button" onClick={() => setEditingHelper(null)} className="btn-secondary" style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div style={{ background: '#f8fafc', border: '2px dashed #cbd5e1', borderRadius: '14px', padding: '1.25rem' }}>
                <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '0.75rem' }}>
                  ➕ Add New Country Visa Helper
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Country Name:</label>
                    <input type="text" placeholder="e.g. Thailand" value={newCountryName} onChange={(e) => setNewCountryName(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Flag Emoji:</label>
                    <input type="text" placeholder="e.g. 🇹🇭" value={newFlag} onChange={(e) => setNewFlag(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Category Label:</label>
                    <input type="text" placeholder="e.g. Official e-Visa" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Visa Types:</label>
                    <input type="text" placeholder="e.g. Tourist / Business" value={newTypes} onChange={(e) => setNewTypes(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Processing Time:</label>
                    <input type="text" placeholder="e.g. 2 - 3 Days" value={newProcTime} onChange={(e) => setNewProcTime(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Badge Tag:</label>
                    <input type="text" placeholder="e.g. Popular / New" value={newBadge} onChange={(e) => setNewBadge(e.target.value)} />
                  </div>
                </div>
                <button type="button" onClick={handleAddNewHelper} className="btn-primary" style={{ fontSize: '0.85rem', padding: '0.6rem 1.25rem' }}>
                  ➕ Save New Visa Helper
                </button>
              </div>
            </div>
          )}

          {/* TAB 4: AGENT PROFIT MARKUP & PRICING CONFIGURATOR */}
          {activeTab === 'MARKUP' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#15803d', margin: 0 }}>
                  💰 Agent Profit Markup & Fee Schedule Configurator
                </h3>

                <select
                  value={selectedReqCountry}
                  onChange={(e) => setSelectedReqCountry(e.target.value)}
                  style={{ width: 'auto', padding: '0.4rem 0.85rem', fontWeight: '700', color: '#15803d', borderColor: '#10b981' }}
                >
                  <option value="INDIA">India 🇮🇳 Pricing</option>
                  <option value="MALAYSIA">Malaysia 🇲🇾 Pricing</option>
                  <option value="VIETNAM">Vietnam 🇻🇳 Pricing</option>
                </select>
              </div>

              <div style={{ background: '#ffffff', border: '2px solid #10b981', borderRadius: '14px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#15803d', marginBottom: '0.75rem' }}>
                  💰 Fee Schedule & Profit Margin per Visa Category ({reqData.countryName})
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {reqData.visaPrices.map((p, idx) => {
                    const govt = p.govtUsd || 0;
                    const markup = p.agentMarkupUsd || 0;
                    const totalUsd = govt + markup;
                    const totalLkr = Math.round(totalUsd * config.exchangeRateLkr);

                    return (
                      <div
                        key={idx}
                        style={{
                          background: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '10px',
                          padding: '0.85rem 1rem',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                          gap: '0.75rem',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: '700', color: '#14532d' }}>{p.name}</div>
                          <input
                            type="text"
                            value={p.description || ''}
                            onChange={(e) => handleUpdateMarkupPrice(idx, 'description', e.target.value)}
                            style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem', color: '#166534', width: '100%', borderRadius: '4px', border: '1px solid #a7f3d0' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#475569' }}>Govt USD Fee:</label>
                          <input
                            type="number"
                            value={govt}
                            onChange={(e) => handleUpdateMarkupPrice(idx, 'govtUsd', parseFloat(e.target.value) || 0)}
                            style={{ padding: '0.35rem 0.5rem', fontSize: '0.85rem' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#15803d' }}>+ Agent Profit Markup ($):</label>
                          <input
                            type="number"
                            value={markup}
                            onChange={(e) => handleUpdateMarkupPrice(idx, 'agentMarkupUsd', parseFloat(e.target.value) || 0)}
                            style={{ padding: '0.35rem 0.5rem', fontSize: '0.85rem', borderColor: '#10b981', background: '#ecfdf5', fontWeight: '700' }}
                          />
                        </div>

                        <div style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => handleToggleHidePrice(idx)}
                            style={{
                              padding: '0.35rem 0.75rem',
                              borderRadius: '6px',
                              border: '1px solid',
                              borderColor: p.isHidden ? '#fca5a5' : '#86efac',
                              background: p.isHidden ? '#fef2f2' : '#f0fdf4',
                              color: p.isHidden ? '#dc2626' : '#15803d',
                              fontWeight: '700',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              marginBottom: '0.4rem',
                            }}
                          >
                            {p.isHidden ? '🙈 Hidden from Public' : '👁️ Visible to Public'}
                          </button>

                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Customer Price:</div>
                          <div style={{ fontSize: '1.15rem', fontWeight: '900', color: '#15803d' }}>
                            LKR {totalLkr.toLocaleString()} Rs
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: REQUIREMENTS & CHECKLIST EDITOR */}
          {activeTab === 'REQUIREMENTS' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  📑 Visa Requirements & Guidelines Checklist
                </h3>

                <select
                  value={selectedReqCountry}
                  onChange={(e) => setSelectedReqCountry(e.target.value)}
                  style={{ width: 'auto', padding: '0.4rem 0.85rem' }}
                >
                  <option value="INDIA">India 🇮🇳</option>
                  <option value="MALAYSIA">Malaysia 🇲🇾</option>
                  <option value="VIETNAM">Vietnam 🇻🇳</option>
                </select>
              </div>

              <div style={{ background: '#f8fafc', border: '1px solid #bfdbfe', borderRadius: '14px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '0.5rem' }}>
                  📑 e-Visa Guidelines Tabs Manager (Show / Hide / Add Custom Tabs)
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                  {getGuidelineTabsForCountry(config, selectedReqCountry).map((t) => (
                    <div
                      key={t.id}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        padding: '0.65rem 0.85rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontSize: '0.875rem', fontWeight: '700', color: t.isHidden ? '#94a3b8' : '#0f172a' }}>
                        {t.label} {t.isHidden && <span style={{ color: '#ef4444' }}>(Hidden)</span>}
                      </span>

                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button
                          type="button"
                          onClick={() => handleToggleHideGuidelineTab(t.id)}
                          style={{
                            padding: '0.35rem 0.75rem',
                            borderRadius: '6px',
                            border: '1px solid',
                            borderColor: t.isHidden ? '#fca5a5' : '#86efac',
                            background: t.isHidden ? '#fef2f2' : '#f0fdf4',
                            color: t.isHidden ? '#dc2626' : '#15803d',
                            fontWeight: '700',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                          }}
                        >
                          {t.isHidden ? '🙈 Hidden' : '👁️ Visible'}
                        </button>

                        {!['TOURIST', 'BUSINESS', 'MEDICAL'].includes(t.id) && (
                          <button
                            type="button"
                            onClick={() => handleDeleteGuidelineTab(t.id)}
                            style={{
                              padding: '0.35rem 0.65rem',
                              borderRadius: '6px',
                              border: '1px solid #fca5a5',
                              background: '#fef2f2',
                              color: '#dc2626',
                              fontSize: '0.75rem',
                              fontWeight: '700',
                              cursor: 'pointer',
                            }}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder={`e.g. 🎤 Conference e-Visa Guidelines for ${selectedReqCountry}`}
                    value={newGuidelineTabLabel}
                    onChange={(e) => setNewGuidelineTabLabel(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="button" onClick={handleAddNewGuidelineTab} className="btn-primary" style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
                    ➕ Add Guidelines Tab
                  </button>
                </div>
              </div>

              {/* DOCUMENT CHECKLIST EDITOR */}
              <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '14px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#1e3a8a', margin: 0 }}>
                    📑 Required Documents Checklist Editor ({reqData.countryName})
                  </h4>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>Category:</label>
                    <select
                      value={selectedDocCategoryTab}
                      onChange={(e) => setSelectedDocCategoryTab(e.target.value)}
                      style={{ width: 'auto', padding: '0.35rem 0.65rem', fontWeight: '700', color: '#1e3a8a', borderColor: '#2563eb' }}
                    >
                      {getGuidelineTabsForCountry(config, selectedReqCountry).map((tab) => (
                        <option key={tab.id} value={tab.id}>
                          {tab.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
                  {getActiveDocList().map((doc, i) => (
                    <div key={i} style={{ background: '#ffffff', padding: '0.5rem 0.85rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      {editingDocIndex === i ? (
                        <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                          <input
                            type="text"
                            value={editingDocValue}
                            onChange={(e) => setEditingDocValue(e.target.value)}
                            style={{ flex: 1, padding: '0.35rem 0.6rem', fontSize: '0.85rem' }}
                          />
                          <button
                            type="button"
                            onClick={() => handleSaveDocumentItem(i)}
                            className="btn-primary"
                            style={{ padding: '0.35rem 0.75rem', fontSize: '0.775rem' }}
                          >
                            💾 Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingDocIndex(null)}
                            className="btn-secondary"
                            style={{ padding: '0.35rem 0.75rem', fontSize: '0.775rem' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.85rem', color: '#334155' }}>• {doc}</span>
                          <div style={{ display: 'flex', gap: '0.35rem' }}>
                            <button
                              type="button"
                              onClick={() => handleStartEditDocumentItem(i, doc)}
                              className="btn-secondary"
                              style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem' }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteDocumentItem(i)}
                              style={{ border: 'none', background: 'transparent', color: '#dc2626', fontWeight: '700', cursor: 'pointer', fontSize: '0.8rem' }}
                            >
                              ✕ Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder={`Add new required document item for ${selectedDocCategoryTab}...`}
                    value={newDocText}
                    onChange={(e) => setNewDocText(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="button" onClick={handleAddDocumentItem} className="btn-primary" style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
                    ➕ Add Item
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: QUESTIONS & FORM RULES */}
          {activeTab === 'QUESTIONS' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  📝 Form Questions & Selection Options Manager
                </h3>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <select
                    value={selectedQuestionsCountry}
                    onChange={(e) => setSelectedQuestionsCountry(e.target.value as any)}
                    style={{ width: 'auto', padding: '0.4rem 0.85rem', fontWeight: '700', color: '#1e3a8a', borderColor: '#2563eb' }}
                  >
                    <option value="INDIA">India 🇮🇳 Helper</option>
                    <option value="MALAYSIA">Malaysia 🇲🇾 Helper</option>
                    <option value="VIETNAM">Vietnam 🇻🇳 Helper</option>
                  </select>

                  <select
                    value={questionsStep}
                    onChange={(e) => setQuestionsStep(parseInt(e.target.value, 10))}
                    style={{ width: 'auto', padding: '0.4rem 0.85rem' }}
                  >
                    <option value={1}>Step 1: Application & Purpose</option>
                    <option value={2}>Step 2: Personal Details</option>
                    <option value={3}>Step 3: Passport Details</option>
                    <option value={4}>Step 4: Contact & Address</option>
                    <option value={5}>Step 5: Family & Occupation</option>
                    <option value={6}>Step 6: Travel & Declarations</option>
                  </select>
                </div>
              </div>

              {editingQuestion && (
                <div style={{ background: '#f0fdf4', border: '2px solid #10b981', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#14532d', marginBottom: '0.75rem' }}>
                    ✏️ Edit Question Field: <span style={{ color: '#047857' }}>{editingQuestion.id}</span> ({editingQuestion.type.toUpperCase()})
                  </h4>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Question Label:</label>
                    <input type="text" value={editQLabel} onChange={(e) => setEditQLabel(e.target.value)} />
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Input Placeholder Text:</label>
                    <input type="text" value={editQPlaceholder} onChange={(e) => setEditQPlaceholder(e.target.value)} placeholder="e.g. MUNAWEERA THANTHRIGE" />
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '700' }}>Tooltip Hint / Guide Text:</label>
                    <input type="text" value={editQTooltip} onChange={(e) => setEditQTooltip(e.target.value)} placeholder="e.g. As shown on passport page 2" />
                  </div>

                  {/* SELECT / RADIO CHOICE OPTIONS EDITOR */}
                  {['select', 'radio', 'checkbox'].includes(editingQuestion.type) && (
                    <div style={{ background: '#ffffff', border: '1px solid #a7f3d0', borderRadius: '10px', padding: '1rem', marginBottom: '1rem' }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: '800', color: '#15803d', display: 'block', marginBottom: '0.5rem' }}>
                        📋 Selection Choices & Options List ({editQOptions.length}):
                      </label>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '200px', overflowY: 'auto', marginBottom: '0.75rem' }}>
                        {editQOptions.map((opt, optIdx) => (
                          <div key={optIdx} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '700', width: '24px' }}>#{optIdx + 1}</span>
                            <input
                              type="text"
                              value={opt}
                              onChange={(e) => handleModifyQuestionOption(optIdx, e.target.value)}
                              style={{ flex: 1, padding: '0.3rem 0.5rem', fontSize: '0.825rem' }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveQuestionOption(optIdx)}
                              style={{ border: 'none', background: 'transparent', color: '#dc2626', fontWeight: '700', cursor: 'pointer', fontSize: '0.8rem' }}
                            >
                              ✕ Remove
                            </button>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <input
                          type="text"
                          placeholder="Add new choice option (e.g. Business Visa 1 Year)..."
                          value={newOptionInput}
                          onChange={(e) => setNewOptionInput(e.target.value)}
                          style={{ flex: 1, padding: '0.35rem 0.6rem', fontSize: '0.85rem' }}
                        />
                        <button
                          type="button"
                          onClick={handleAddQuestionOption}
                          className="btn-primary"
                          style={{ padding: '0.35rem 0.85rem', fontSize: '0.8rem', background: '#10b981' }}
                        >
                          ➕ Add Choice Option
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" onClick={handleSaveQuestionEdit} className="btn-primary" style={{ fontSize: '0.85rem', padding: '0.45rem 1rem' }}>
                      💾 Save Question & Options
                    </button>
                    <button type="button" onClick={() => setEditingQuestion(null)} className="btn-secondary" style={{ fontSize: '0.85rem', padding: '0.45rem 1rem' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                {questionsList
                  .filter((q) => q.step === questionsStep)
                  .map((q) => (
                    <div
                      key={q.id}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '10px',
                        padding: '0.85rem 1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <span style={{ fontSize: '0.75rem', fontWeight: '700', background: '#e2e8f0', padding: '0.2rem 0.5rem', borderRadius: '4px', marginRight: '0.5rem' }}>
                          {q.type.toUpperCase()}
                        </span>
                        <strong style={{ fontSize: '0.9rem', color: '#0f172a' }}>{q.label}</strong>
                        {q.options && q.options.length > 0 && (
                          <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '0.5rem' }}>
                            ({q.options.length} options)
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleStartEditQuestion(q)}
                        className="btn-secondary"
                        style={{ padding: '0.35rem 0.65rem', fontSize: '0.775rem' }}
                      >
                        ✏️ Edit & Choices
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* TAB 4: SETTINGS & CURRENCY PRIVACY */}
          {activeTab === 'SETTINGS' && (
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', marginBottom: '1rem' }}>
                💱 Global System Settings & Currency Privacy Configuration
              </h3>

              <div style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '700', color: '#0f172a', marginBottom: '0.35rem' }}>
                  1 USD to Sri Lankan Rupee (LKR) Exchange Rate:
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: '800', color: '#15803d' }}>$1 USD =</span>
                  <input
                    type="number"
                    value={exchangeRateInput}
                    onChange={(e) => setExchangeRateInput(parseFloat(e.target.value) || 340)}
                    style={{ width: '160px', fontSize: '1.1rem', fontWeight: '800', color: '#15803d' }}
                  />
                  <span style={{ fontSize: '1.1rem', fontWeight: '800', color: '#15803d' }}>LKR</span>
                </div>
              </div>

              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={hideUsdInput}
                    onChange={(e) => setHideUsdInput(e.target.checked)}
                    style={{ width: '20px', height: '20px', accentColor: '#10b981' }}
                  />
                  <div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#14532d' }}>
                      🔒 Hide USD values from public applicants (Show ONLY LKR prices)
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#166534' }}>
                      When checked, public applicants will see only clean LKR prices (e.g. LKR 11,900 Rs).
                    </div>
                  </div>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button type="button" onClick={handleSaveSettings} className="btn-primary">
                  💾 Save System Settings
                </button>

                <button type="button" onClick={handleResetDefaults} className="btn-secondary" style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
                  🔄 Reset to Factory Defaults
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* VIEW SUBMISSION DETAILS MODAL */}
      {selectedSubApp && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(4px)',
            zIndex: 100000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '650px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              padding: '1.5rem',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  📄 Application Record #{selectedSubApp.id}
                </h3>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Submitted: {selectedSubApp.submittedAt} | Status: <strong>{selectedSubApp.status}</strong>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedSubApp(null)}
                style={{ border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.85rem' }}>
              <div><strong>Applicant Name:</strong> {selectedSubApp.applicantName}</div>
              <div><strong>Passport Number:</strong> {selectedSubApp.passportNumber}</div>
              <div><strong>Nationality:</strong> {selectedSubApp.nationality}</div>
              <div><strong>Destination:</strong> {selectedSubApp.countryName}</div>
              <div><strong>Visa Category:</strong> {selectedSubApp.visaCategory}</div>
              <div><strong>Fee Total:</strong> LKR {selectedSubApp.totalFeeLkr?.toLocaleString()} Rs</div>
            </div>

            <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#1e3a8a', marginBottom: '0.5rem' }}>
              📋 Form Fields Data:
            </h4>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '0.85rem', borderRadius: '8px', fontSize: '0.8rem', maxHeight: '240px', overflowY: 'auto', marginBottom: '1rem' }}>
              {Object.entries(selectedSubApp.data || {}).map(([k, v]) => (
                <div key={k} style={{ marginBottom: '0.25rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.2rem' }}>
                  <strong style={{ color: '#475569' }}>{k}:</strong> {Array.isArray(v) ? v.join(', ') : String(v)}
                </div>
              ))}
            </div>

            <div style={{ textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => setSelectedSubApp(null)}
                className="btn-primary"
                style={{ padding: '0.5rem 1.25rem', fontSize: '0.85rem' }}
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
